import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapPin, Navigation, RefreshCw, ChefHat, Wifi, Search, UtensilsCrossed } from 'lucide-react';
import { ONLINE_KITCHENS_URL, NEARBY_KITCHENS_URL } from '../../config/api';
import { getStoredCoords } from '../Utils/location';
import Skeleton from '../Utils/Skeleton';
import './BrowseKitchens.css';

interface OnlineKitchen {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_photo: string;
  kitchen_type: string;
  kitchen_category: string;
  city: string;
  state: string;
  country: string;
  status: boolean;
  made_by: string;
  distance_km?: number;
}

interface BrowseKitchensProps {
  onSelectKitchen: (kitchenId: number | null, kitchenName: string | null) => void;
  selectedKitchenId: number | null;
  locationVersion?: number;
}

type ViewMode = 'all' | 'nearby';
type RadiusKm = 5 | 10 | 15 | 20 | 25 | 30;
type SortMode = 'distance' | 'name';

const RADIUS_OPTIONS: RadiusKm[] = [5, 10, 15, 20, 25, 30];

const BrowseKitchens: React.FC<BrowseKitchensProps> = ({ onSelectKitchen, selectedKitchenId, locationVersion = 0 }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('nearby');
  const [kitchens, setKitchens] = useState<OnlineKitchen[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(10);
  const [locationDenied, setLocationDenied] = useState(false);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState<string>('all');
  const [sortMode, setSortMode] = useState<SortMode>('distance');

  const fetchAllKitchens = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(ONLINE_KITCHENS_URL, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: OnlineKitchen[] = await res.json();
      setKitchens(Array.isArray(data) ? data : []);
    } catch {
      setError('Could not load kitchens. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchNearbyKitchens = useCallback(async (lat: number, lng: number, radius: RadiusKm) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${NEARBY_KITCHENS_URL}?latitude=${lat}&longitude=${lng}&radius_km=${radius}`;
      const res = await fetch(url, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list: OnlineKitchen[] = Array.isArray(data?.kitchens) ? data.kitchens : [];
      setKitchens(list);
      if (list.length === 0) {
        setError(`No kitchens found within ${radius} km. Try increasing the radius.`);
      }
    } catch {
      setError('Could not load nearby kitchens. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requestLocation = useCallback(() => {
    setError(null);
    setLocationDenied(false);

    const stored = getStoredCoords();
    if (stored) {
      const coords = { lat: stored.lat, lng: stored.lon };
      setUserCoords(coords);
      fetchNearbyKitchens(coords.lat, coords.lng, radiusKm);
      return;
    }

    if (!navigator.geolocation) {
      setError('Your browser does not support location detection. Showing all online kitchens instead.');
      setViewMode('all');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserCoords(coords);
        fetchNearbyKitchens(coords.lat, coords.lng, radiusKm);
      },
      () => {
        setLocationDenied(true);
        setViewMode('all');
      },
      { timeout: 10000 }
    );
  }, [fetchNearbyKitchens, radiusKm]);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllKitchens();
    } else {
      const stored = getStoredCoords();
      if (stored) {
        const coords = { lat: stored.lat, lng: stored.lon };
        setUserCoords(coords);
        fetchNearbyKitchens(coords.lat, coords.lng, radiusKm);
      } else if (userCoords) {
        fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm);
      } else {
        requestLocation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationVersion]);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllKitchens();
    } else {
      const stored = getStoredCoords();
      if (stored) {
        const coords = { lat: stored.lat, lng: stored.lon };
        setUserCoords(coords);
        fetchNearbyKitchens(coords.lat, coords.lng, radiusKm);
      } else if (userCoords) {
        fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm);
      } else {
        requestLocation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === 'nearby' && userCoords) {
      fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusKm]);

  const kitchenTypes = useMemo(() => {
    const set = new Set<string>();
    kitchens.forEach(k => { if (k.kitchen_type) set.add(k.kitchen_type); });
    return Array.from(set).sort();
  }, [kitchens]);

  const filteredKitchens = useMemo(() => {
    let result = kitchens;
    if (activeType !== 'all') {
      result = result.filter(k => k.kitchen_type === activeType);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(k =>
        k.kitchen_name.toLowerCase().includes(q) ||
        k.kitchen_category?.toLowerCase().includes(q) ||
        k.city?.toLowerCase().includes(q)
      );
    }
    if (sortMode === 'name') {
      result = [...result].sort((a, b) => a.kitchen_name.localeCompare(b.kitchen_name));
    } else if (sortMode === 'distance' && viewMode === 'nearby') {
      result = [...result].sort((a, b) => (a.distance_km ?? 999) - (b.distance_km ?? 999));
    }
    return result;
  }, [kitchens, activeType, searchQuery, sortMode, viewMode]);

  const handleKitchenClick = (kitchenId: number, kitchenName: string) => {
    const isDeselecting = selectedKitchenId === kitchenId;
    const next = isDeselecting ? null : kitchenId;
    onSelectKitchen(next, isDeselecting ? null : kitchenName);
    if (next !== null) {
      setTimeout(() => {
        const mealsSection = document.getElementById('featured-meals');
        if (mealsSection) {
          mealsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 80);
    }
  };

  return (
    <section className="browse-kitchens">
      <div className="bk-inner">
        {/* Header */}
        <div className="bk-header">
          <div className="bk-title-block">
            <h2 className="bk-title">Browse Kitchens</h2>
            <p className="bk-subtitle">
              Discover home kitchens cooking near you right now
              {!isLoading && kitchens.length > 0 && (
                <span className="bk-count"> &bull; {kitchens.length} kitchen{kitchens.length !== 1 ? 's' : ''} online</span>
              )}
            </p>
          </div>

          {/* View toggle */}
          <div className="bk-toggle">
            <button
              className={`bk-toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
              onClick={() => setViewMode('all')}
            >
              <Wifi size={15} />
              All Online
            </button>
            <button
              className={`bk-toggle-btn ${viewMode === 'nearby' ? 'active' : ''}`}
              onClick={() => setViewMode('nearby')}
            >
              <Navigation size={15} />
              Near Me
            </button>
          </div>
        </div>

        {/* Nearby controls */}
        {viewMode === 'nearby' && (
          <div className="bk-nearby-controls">
            <span className="bk-radius-label">Radius:</span>
            {RADIUS_OPTIONS.map(r => (
              <button
                key={r}
                className={`bk-radius-btn ${radiusKm === r ? 'active' : ''}`}
                onClick={() => setRadiusKm(r)}
              >
                {r} km
              </button>
            ))}
            <button
              className="bk-refresh-btn"
              onClick={() => userCoords ? fetchNearbyKitchens(userCoords.lat, userCoords.lng, radiusKm) : requestLocation()}
              disabled={isLoading}
              title="Refresh"
            >
              <RefreshCw size={14} className={isLoading ? 'bk-spin' : ''} />
            </button>
          </div>
        )}

        {/* Search & filter controls */}
        {!isLoading && kitchens.length > 0 && (
          <div className="bk-search-filter-row">
            <div className="bk-search-wrap">
              <Search size={15} className="bk-search-icon" />
              <input
                type="text"
                className="bk-search-input"
                placeholder="Search kitchens by name, category, or city..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="bk-search-clear" onClick={() => setSearchQuery('')}>
                  &times;
                </button>
              )}
            </div>
            {kitchenTypes.length > 1 && (
              <div className="bk-type-chips">
                <button
                  className={`bk-type-chip ${activeType === 'all' ? 'bk-type-chip--active' : ''}`}
                  onClick={() => setActiveType('all')}
                >
                  All Types
                </button>
                {kitchenTypes.map(t => (
                  <button
                    key={t}
                    className={`bk-type-chip ${activeType === t ? 'bk-type-chip--active' : ''}`}
                    onClick={() => setActiveType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            {viewMode === 'nearby' && (
              <select
                className="bk-sort-select"
                value={sortMode}
                onChange={e => setSortMode(e.target.value as SortMode)}
              >
                <option value="distance">Sort: Distance</option>
                <option value="name">Sort: Name A-Z</option>
              </select>
            )}
          </div>
        )}

        {/* Location denied notice */}
        {locationDenied && (
          <div className="bk-notice">
            <MapPin size={16} />
            <span>Location access was denied. Showing all online kitchens instead. Enable location in your browser settings to use the Near Me feature.</span>
          </div>
        )}

        {/* Active filter banner */}
        {selectedKitchenId !== null && (
          <div className="bk-filter-banner">
            <span>
              Showing food from <strong>{kitchens.find(k => k.kitchen_id === selectedKitchenId)?.kitchen_name || `Kitchen #${selectedKitchenId}`}</strong>
            </span>
            <button className="bk-clear-filter" onClick={() => onSelectKitchen(null, null)}>
              Clear filter
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="bk-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bk-skeleton-card">
                <Skeleton width="100%" height="120px" borderRadius="0" />
                <div className="bk-skeleton-body">
                  <Skeleton width="80%" height="0.875rem" style={{ marginBottom: '0.3rem' }} />
                  <Skeleton width="50%" height="0.75rem" style={{ marginBottom: '0.3rem' }} />
                  <Skeleton width="60%" height="0.73rem" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error (non-blocking) */}
        {error && !isLoading && (
          <div className="bk-error">{error}</div>
        )}

        {/* Kitchen cards grid */}
        {!isLoading && filteredKitchens.length > 0 && (
          <div className="bk-grid">
            {filteredKitchens.map(kitchen => (
              <button
                key={kitchen.kitchen_id}
                className={`bk-card ${selectedKitchenId === kitchen.kitchen_id ? 'selected' : ''}`}
                onClick={() => handleKitchenClick(kitchen.kitchen_id, kitchen.kitchen_name)}
              >
                <div className="bk-card-img-wrap">
                  {kitchen.kitchen_photo ? (
                    <img
                      src={kitchen.kitchen_photo}
                      alt={kitchen.kitchen_name}
                      className="bk-card-img"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('bk-img-fallback--hidden');
                      }}
                    />
                  ) : null}
                  <div className={`bk-img-fallback ${kitchen.kitchen_photo ? 'bk-img-fallback--hidden' : ''}`}>
                    <ChefHat size={32} />
                  </div>
                  <span className="bk-status-dot" title="Online" />
                  {kitchen.distance_km !== undefined && (
                    <span className="bk-distance-badge">
                      <MapPin size={10} />
                      {kitchen.distance_km === 0 ? '<1 km' : `${kitchen.distance_km.toFixed(1)} km`}
                    </span>
                  )}
                </div>

                <div className="bk-card-body">
                  <p className="bk-card-name">{kitchen.kitchen_name}</p>
                  <p className="bk-card-category">{kitchen.kitchen_category}</p>
                  <p className="bk-card-city">
                    <MapPin size={11} />
                    {[kitchen.city, kitchen.state].filter(Boolean).join(', ')}
                  </p>
                </div>

                {selectedKitchenId === kitchen.kitchen_id && (
                  <div className="bk-card-selected-label">Viewing food</div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* No results after filtering */}
        {!isLoading && !error && kitchens.length > 0 && filteredKitchens.length === 0 && (
          <div className="bk-empty">
            <Search size={48} />
            <p>No kitchens match your search. Try a different term or clear filters.</p>
            <button
              className="bk-empty-cta"
              onClick={() => { setSearchQuery(''); setActiveType('all'); }}
            >
              Clear filters
            </button>
          </div>
        )}

        {/* No kitchens at all */}
        {!isLoading && !error && kitchens.length === 0 && (
          <div className="bk-empty">
            <UtensilsCrossed size={48} />
            <p>No kitchens online right now. Check back soon!</p>
            {viewMode === 'nearby' && (
              <button className="bk-empty-cta" onClick={() => setViewMode('all')}>
                Try All Online Kitchens
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BrowseKitchens;
